"use client";
import React from "react";
import Avatar from "../primitives/avatar";
import Link from "next/link";
import * as Collapsible from "@radix-ui/react-collapsible";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import Icon from "../primitives/icon";
import CommentForm from "./comment-form";
import {
  GameDiscussionCommentModel,
  GameDiscussionBettorModel,
  Sport,
} from "common";
import { createProfileSlug } from "@/utils/url";
import RelativeTime from "../relative-time";
import list from "@/components/primitives/list.module.css";
import mlClient from "@/ml-client";
import { useAuth } from "@clerk/clerk-react";
import { formatUnits } from "@/utils";

export default function GameComment({
  sport,
  gameId,
  data,
  bettors,
  disabled,
  isReply,
}: {
  sport: Sport;
  gameId: number;
  data: GameDiscussionCommentModel;
  bettors: { [key: number]: GameDiscussionBettorModel };
  disabled?: boolean;
  isReply?: boolean;
}) {
  const { getToken } = useAuth();
  const [open, setOpen] = React.useState<boolean>(false);
  const [openActions, setOpenActions] = React.useState<boolean>(false);
  const [replying, setReplying] = React.useState<boolean>(false);
  const [justReplied, setJustReplied] = React.useState<boolean>(false);
  const [reported, setReported] = React.useState<boolean>(
    data.reported_by ? true : false
  );
  const replyCount = data.replies?.length || 0;
  const hasReplies = replyCount > 0;

  const bettor = bettors[data.bettor_id];

  function handleReply() {
    setReplying((prev) => !prev);
  }

  async function handleReport(commentId: number) {
    try {
      const token = await getToken();
      const baseUrl: string = `admin/game-comments/${commentId}/report`;
      const params: URLSearchParams = new URLSearchParams();
      const request: string = `${baseUrl}?${params}`;
      const response = await mlClient.put(request, undefined, token);
      if (response.ok) {
        setReported(true);
      }
    } catch (err) {
      console.error(err);
      console.log("Failed to report comment. Please try again.");
    } finally {
      console.log("Comment reported:", commentId);
    }
  }

  React.useEffect(() => {
    if (justReplied && hasReplies) {
      setOpen(true);
      setJustReplied(false);
    }
  }, [justReplied, hasReplies]);

  return (
    <div className="flex flex-col w-full desktop-grid:flex-row justify-between items-start desktop-grid:items-center mb-2">
      <div className="flex-col w-full">
        <div className="flex flex-row gap-2 w-full">
          {bettor ? (
            <Link href={createProfileSlug(bettor.displayName)}>
              <Avatar img={bettor.avatarUrl}></Avatar>
            </Link>
          ) : (
            <Avatar />
          )}
          <div className="flex flex-col gap-1">
            <div className="flex flex-row items-center gap-2 flex-wrap">
              {bettor ? (
                <Link
                  href={createProfileSlug(bettor.displayName)}
                  className="text-title-sm-medium text-dark"
                >
                  {bettor.displayName}
                </Link>
              ) : (
                <span className="text-title-sm-medium text-secondary">
                  Unknown Bettor
                </span>
              )}
              <div className="flex flex-row gap-2 items-center">
                {bettor?.activePicksOnGame > 0 ? (
                  <div className="h-5 px-2 bg-surface-400 flex items-center rounded-sm">
                    <span className="text-label-lg-medium active-picks-text">
                      {bettor.activePicksOnGame} Pick
                      {bettor.activePicksOnGame === 1 ? "" : "s"}
                    </span>
                  </div>
                ) : undefined}
                  <div className="flex items-center rounded-sm">
                    <span className="text-label-lg text-secondary-500">
                      {formatUnits(bettor.totalUnits)} Units (Last 25)
                    </span>
                  </div>
              </div>
            </div>
            <div className="text-sm text-dark">{data.message}</div>
            <div className="flex flex-row gap-0 items-center">
              {data.created_at && (
                <div className="text-title-sm text-secondary mr-1">
                  <RelativeTime date={data.created_at} />
                </div>
              )}
              {!disabled && !isReply && (
                <button
                  onClick={handleReply}
                  className="focus-visible:ring text-label-lg-bold text-secondary-600 uppercase rounded-full py-1 px-0 md:px-2 hover:bg-surface-400"
                >
                  {replying ? "Cancel" : "Reply"}
                </button>
              )}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger className="outline-none fill-secondary-500 rounded-full px-2 background-white hover:bg-surface-400">
                  <Icon name="navigation-menu-horizontal" size="sm" />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="start"
                    side="top"
                    sideOffset={8}
                    className="rounded-md bg-light shadow-elevation-300 focus:shadow-elevation-400 will-change-[transform,opacity] data-[state=open]:data-[side=top]:animate-slideDownAndFade data-[state=open]:data-[side=right]:animate-slideLeftAndFade data-[state=open]:data-[side=bottom]:animate-slideUpAndFade data-[state=open]:data-[side=left]:animate-slideRightAndFade z-50 w-[12.5rem] p-2"
                  >
                    <button
                      className={`${list.listItem} w-full btn`}
                      disabled={reported ? true : false}
                      data-disabled={reported ? "true" : undefined}
                      onClick={(e) => handleReport(data.id)}
                    >
                      <div className="flex flex-row items-center gap-2">
                        <Icon name="flag" size="sm" />
                        <span>
                          {reported ? "Comment reported" : "Report Comment"}
                        </span>
                      </div>
                    </button>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          </div>
        </div>
        {replying && !disabled && (
          <div className="pl-10 md:pl-10 pb-1">
            <CommentForm
              sport={sport}
              gameId={gameId}
              replyingTo={data.id}
              disabled={disabled || false}
              setReplying={setReplying}
              setJustReplied={setJustReplied}
            />
          </div>
        )}
        {hasReplies && (
          <Collapsible.Root
            onOpenChange={setOpen}
            className="group pl-10 md:pl-10 overflow-hidden"
          >
            <Collapsible.Trigger asChild>
              <button className="focus-visible:ring z-[1] relative flex flex-row gap-[10px] text-title-sm-medium text-secondary-300 rounded-full hover:bg-primary-600 items-center px-0 md:px-3 py-2">
                {open ? (
                  <Icon name="caret-up" size="sm" />
                ) : (
                  <Icon name="caret-down" size="sm" />
                )}
                {replyCount} {`${replyCount === 1 ? "Reply" : "Replies"}`}
              </button>
            </Collapsible.Trigger>
            <Collapsible.Content className="data-[state=open]:animate-unCollapse data-[state=closed]:animate-collapse bg-white relative z-0 rounded-bl-lg rounded-br-lg">
              <div className="pt-2">
                {data.replies?.map((childComment) => (
                  <GameComment
                    sport={sport}
                    key={childComment.id}
                    gameId={gameId}
                    data={childComment}
                    bettors={bettors}
                    disabled={disabled}
                    isReply={true}
                  />
                ))}
              </div>
            </Collapsible.Content>
          </Collapsible.Root>
        )}
      </div>
    </div>
  );
}
